import { Injectable, inject } from '@angular/core';

import { I18nService } from '@core/i18n-legacy';
import type { Field, FieldTemplate } from '@core/profile-fields-types';
import { FieldKind } from '@core/profile-fields-types';
import type { DropdownItem, IconName } from '@core/user-interface-legacy';

@Injectable({ providedIn: 'root' })
export class ProfileFieldsHelperService {
  private readonly i18n = inject(I18nService);

  public readonly icons: Record<string, IconName> = {
    single_line: 'short-text',
    multi_line: 'format-align-left',
    boolean: 'radio-button-checked',
    date: 'calendar-today',
    dropdown: 'done',
    address: 'my-location',
    gender: 'wc',
    date_of_birth: 'cake-birthday',
    nationality: 'flag',
    language_skill: 'translate',
    skills: 'label',
    number: 'numeric',
    salary: 'cash',
  };

  public readonly fieldNames: { [key: string]: string } = {
    single_line: this.i18n.translate('app.custom_fields.types.single_line'),
    multi_line: this.i18n.translate('app.custom_fields.types.multi_line'),
    boolean: this.i18n.translate('app.custom_fields.types.boolean'),
    date: this.i18n.translate('app.custom_fields.types.date'),
    dropdown: this.i18n.translate('app.custom_fields.types.dropdown'),
    number: this.i18n.translate('app.custom_fields.types.number'),
    salary: this.i18n.translate('app.custom_fields.types.salary'),
    address: this.i18n.translate('app.custom_fields.types.address'),
    date_of_birth: this.i18n.translate('app.custom_fields.types.date_of_birth'),
    gender: this.i18n.translate('app.custom_fields.types.gender'),
    language_skill: this.i18n.translate('app.custom_fields.types.language_skill'),
    nationality: this.i18n.translate('app.custom_fields.types.nationality'),
    skills: this.i18n.translate('app.custom_fields.types.skills'),
  };

  public readonly fieldKinds: FieldKind[] = Object.values(FieldKind).filter(
    f => f !== 'education' && f !== 'experience',
  );

  public readonly alwaysAddableKinds: string[] = [
    'single_line',
    'multi_line',
    'boolean',
    'dropdown',
    'date',
    'number',
    'salary',
  ];

  public readonly anonymizedFields: FieldKind[] = [
    FieldKind.Address,
    FieldKind.Nationality,
    FieldKind.Gender,
    FieldKind.DateOfBirth,
  ];

  public getAddableFields(
    fields: Array<FieldTemplate | Field>,
    includeDropdown: boolean = true,
  ): DropdownItem[] {
    const presentKinds: FieldKind[] = [...new Set(fields.map(field => field.kind))];
    let addableKinds: string[] = [
      ...new Set([
        ...this.alwaysAddableKinds,
        ...this.fieldKinds.filter(kind => !presentKinds.includes(kind)),
      ]),
    ];
    addableKinds = addableKinds.filter(kind => {
      if (kind === FieldKind.Dropdown) return includeDropdown;
      return true;
    });

    return addableKinds.map(kind => ({
      option: this.fieldNames[kind],
      value: kind,
      icon: this.icons[kind],
    }));
  }
}
